import React, { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import type { SupplierDto, User } from '../../types';
import {
    ElkoConfiguration,
    ITPartnerConfiguration,
    KlavtorgConfiguration,
    MerlionConfiguration,
    NagConfiguration,
    NVPrintConfiguration,
    OCSConfiguration,
    RamisConfiguration,
    ResursMediaConfiguration,
    SoftTronikConfiguration,
    VttConfiguration
} from './SupplierConfigurationComponents';
import WarehouseInput from './WarehouseInput';
import AdminJsonEditor from './AdminJsonEditor';

// Supplier type registry to replace repetitive if-else chains
const SUPPLIER_CONFIG_REGISTRY: Record<string, {
    configFields: string[];
    component: React.ComponentType<{ configuration: Record<string, unknown>; onChange: (field: string, value: string | number | unknown) => void; }>;
}> = {
    'elko': { configFields: ElkoConfiguration.configFields, component: ElkoConfiguration },
    'itpartner': { configFields: ITPartnerConfiguration.configFields, component: ITPartnerConfiguration },
    'it partner': { configFields: ITPartnerConfiguration.configFields, component: ITPartnerConfiguration },
    'klavtorg': { configFields: KlavtorgConfiguration.configFields, component: KlavtorgConfiguration },
    'vtt': { configFields: VttConfiguration.configFields, component: VttConfiguration },
    'merlion': { configFields: MerlionConfiguration.configFields, component: MerlionConfiguration },
    'nag': { configFields: NagConfiguration.configFields, component: NagConfiguration },
    'nvprint': { configFields: NVPrintConfiguration.configFields, component: NVPrintConfiguration },
    'nv print': { configFields: NVPrintConfiguration.configFields, component: NVPrintConfiguration },
    'ocs': { configFields: OCSConfiguration.configFields, component: OCSConfiguration },
    'ramis': { configFields: RamisConfiguration.configFields, component: RamisConfiguration },
    'resurs': { configFields: ResursMediaConfiguration.configFields, component: ResursMediaConfiguration },
    'resursmedia': { configFields: ResursMediaConfiguration.configFields, component: ResursMediaConfiguration },
    'softtronik': { configFields: SoftTronikConfiguration.configFields, component: SoftTronikConfiguration },
    'soft tronik': { configFields: SoftTronikConfiguration.configFields, component: SoftTronikConfiguration },
};

// Supplier Edit Modal Component
const SupplierModal: React.FC<{
    supplier?: SupplierDto;
    currentUser: User | null;
    onSave: (supplierType: number, data: string) => Promise<void>;
    onClose: () => void;
    isOpen: boolean;
}> = ({ supplier, currentUser, onSave, onClose, isOpen }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<{
        useWarehouses: boolean;
        warehouses?: string[];
        supplierConfiguration: Record<string, unknown>;
    }>({
        useWarehouses: false,
        warehouses: [],
        supplierConfiguration: {}
    });

    const [spoilerExpanded, setSpoilerExpanded] = useState(false);
    const [jsonData, setJsonData] = useState('');
    const [jsonError, setJsonError] = useState('');

    const isAdmin = useMemo(() => currentUser?.roles?.includes('admin') || false, [currentUser?.roles]);

    // Helper function to get config registry entry
    const getSupplierConfig = useCallback((supplierName: string) => {
        for (const [key, value] of Object.entries(SUPPLIER_CONFIG_REGISTRY)) {
            if (supplierName.includes(key)) {
                return value;
            }
        }
        return null;
    }, []);

    // Memoized supplier config
    const supplierConfigEntry = useMemo(() => {
        return supplier ? getSupplierConfig(supplier.name.toLowerCase()) : null;
    }, [supplier, getSupplierConfig]);

    // Memoized unique warehouses
    const cleanedWarehouses = useMemo(() => {
        return [...new Set(formData.warehouses?.map(w => w.trim()).filter(w => w) || [])];
    }, [formData.warehouses]);

    // Update JSON data when form data changes
    useEffect(() => {
        if (!spoilerExpanded) {
            const finalFormData = {
                useWarehouses: formData.useWarehouses,
                warehouses: cleanedWarehouses,
                supplierConfiguration: formData.supplierConfiguration
            };
            setJsonData(JSON.stringify(finalFormData, null, 2));
        }
    }, [formData, spoilerExpanded, cleanedWarehouses]);

    // Initialize or update form data from supplier
    useEffect(() => {
        if (supplier?.data) {
            try {
                const config = JSON.parse(supplier.data);
                const supplierConfigEntry = getSupplierConfig(supplier.name.toLowerCase());

                const supplierConfig: Record<string, unknown> = {};
                if (supplierConfigEntry) {
                    supplierConfigEntry.configFields.forEach(field => {
                        if (config.supplierConfiguration && config.supplierConfiguration[field] !== undefined) {
                            supplierConfig[field] = config.supplierConfiguration[field];
                        }
                    });
                }

                setFormData({
                    useWarehouses: config.useWarehouses || false,
                    warehouses: config.warehouses || [],
                    supplierConfiguration: supplierConfig
                });
                setJsonData(JSON.stringify(config, null, 2));
            } catch (error) {
                console.error('Error parsing supplier data:', error);
                toast.error('Ошибка при загрузке данных поставщика');
                setFormData({
                    useWarehouses: false,
                    warehouses: [],
                    supplierConfiguration: {}
                });
                setJsonData('{}');
            }
        } else {
            setFormData({
                useWarehouses: false,
                warehouses: [],
                supplierConfiguration: {}
            });
            setJsonData('{}');
        }
        setSpoilerExpanded(false);
        setJsonError('');
    }, [supplier, getSupplierConfig]);

    // Handle escape key to close modal
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isOpen, onClose]);

    const handleSupplierConfigChange = useCallback((field: string, value: string | number | unknown) => {
        setFormData(prev => ({
            ...prev,
            supplierConfiguration: {
                ...prev.supplierConfiguration,
                [field]: value
            }
        }));
    }, []);

    const handleSave = useCallback(async () => {
        if (!supplier) return;

        // Prevent saving if spoiler is expanded and there's a JSON error
        if (spoilerExpanded && jsonError) {
            toast.error('Неверный формат JSON. Исправьте ошибки перед сохранением.');
            return;
        }

        let dataToSave: string;

        // If admin has edited JSON and it's valid, use the JSON data
        if (isAdmin && spoilerExpanded && jsonData.trim() && !jsonError) {
            try {
                JSON.parse(jsonData); // Validate JSON
                dataToSave = jsonData;
            } catch {
                toast.error('Неверный формат JSON. Исправьте ошибки перед сохранением.');
                return;
            }
        } else {
            // Use the form data
            const finalFormData = {
                useWarehouses: formData.useWarehouses,
                warehouses: cleanedWarehouses,
                supplierConfiguration: formData.supplierConfiguration
            };
            dataToSave = JSON.stringify(finalFormData);
        }

        setLoading(true);
        try {
            await onSave(supplier.supplierType, dataToSave);
            onClose();
        } catch (error) {
            console.error('Error updating supplier:', error);
            toast.error('Ошибка при сохранении поставщика');
        } finally {
            setLoading(false);
        }
    }, [supplier, spoilerExpanded, jsonError, isAdmin, jsonData, formData, cleanedWarehouses, onSave, onClose]);

    const addWarehouse = useCallback((warehouse: string) => {
        if (!formData.warehouses?.includes(warehouse)) {
            setFormData(prev => ({
                ...prev,
                warehouses: [...(prev.warehouses || []), warehouse]
            }));
        }
    }, [formData.warehouses]);

    const removeWarehouse = useCallback((warehouseToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            warehouses: prev.warehouses?.filter(w => w !== warehouseToRemove) || []
        }));
    }, []);

    if (!isOpen || !supplier) return null;

    return (
        <div className="modal-backdrop-custom" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="modal-dialog-custom animate-in">
                <div className="modal-content-custom">
                <div className="modal-header-custom">
                    <div className="d-flex align-items-center">
                        <div className="supplier-avatar mr-3">
                            {supplier.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <h3 className="modal-title-custom">Редактирование поставщика</h3>
                            <p className="modal-subtitle-custom">Управление настройками поставщика</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn-close-custom"
                        onClick={onClose}
                        disabled={loading}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
                    <div className="modal-body-custom">
                        <div className="form-section">
                            <div className="form-group-custom">
                                <label className="form-check-custom">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={formData.useWarehouses}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            useWarehouses: e.target.checked
                                        }))}
                                        disabled={loading}
                                    />
                                    <span className="form-check-label">Использовать склады</span>
                                    <span className="label-icon">📦</span>
                                </label>
                            </div>

                            {formData.useWarehouses && (
                                <div className="form-group-custom">
                                    <WarehouseInput
                                        warehouses={formData.warehouses || []}
                                        onAddWarehouse={addWarehouse}
                                        onRemoveWarehouse={removeWarehouse}
                                        disabled={loading}
                                    />
                                </div>
                            )}

                            {/* Supplier Configuration */}
                            {supplierConfigEntry?.component ? (
                                <supplierConfigEntry.component
                                    configuration={formData.supplierConfiguration}
                                    onChange={handleSupplierConfigChange}
                                />
                            ) : null}
                        </div>

                        <div className="supplier-info">
                            <div className="info-item">
                                <div className="info-label">ID поставщика</div>
                                <div className="info-value">#{supplier.supplierType}</div>
                            </div>
                            <div className="info-item">
                                <div className="info-label">Название</div>
                                <div className="info-value">{supplier.name}</div>
                            </div>
                        </div>

                        {/* Admin JSON Editor */}
                        {isAdmin && (
                            <AdminJsonEditor
                                jsonData={jsonData}
                                spoilerExpanded={spoilerExpanded}
                                jsonError={jsonError}
                                loading={loading}
                                onSpoilerToggle={() => setSpoilerExpanded(!spoilerExpanded)}
                                onJsonChange={(json, error) => {
                                    setJsonData(json);
                                    setJsonError(error);
                                }}
                            />
                        )}
                    </div>

                    <div className="modal-footer-custom">
                        <div className="d-flex justify-content align-items-center w-100">
                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn-secondary-custom"
                                    onClick={onClose}
                                    disabled={loading}
                                >
                                    <span className="btn-icon">✕</span>
                                    Отмена
                                </button>
                                <button
                                    type="button"
                                    className="btn-primary-custom"
                                    onClick={handleSave}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="btn-spinner"></span>
                                            Сохранение...
                                        </>
                                    ) : (
                                        <>
                                            <span className="btn-icon">✓</span>
                                            Сохранить настройки
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupplierModal;
